import { PlusFillIcon, TrashIcon } from "../../shared/Icons";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { countries } from "../../shared/const/countries.const";
import { nationalities } from "../../shared/const/nationalities.const";
import { api, apiUrl } from "../../shared/services/service";
import { generateUUID } from "../../shared/services/utils";
import {
  Bq3CrewManagementM,
  ManningAgentOfficerM,
  ManningAgentRatingM,
  NationalityOfRatingM,
  initialBq3CrewManagement,
  mapSqlToBq3CrewManagementForm,
} from "../models/bq3-crew-management-form.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF3CrewManagementAtom } from "../state/baltic-form.atoms";
import { useAtom, useAtomValue } from "jotai";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq3CrewManagement: React.FC<Props> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF3CrewManagementAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq3CrewManagementM>(initialBq3CrewManagement);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  const updateRatingNationality = (idx: number, field: keyof NationalityOfRatingM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.nationalityOfRatings];
      (updated[idx] as any)[field] = value;
      return { ...prev, nationalityOfRatings: updated };
    });
  };

  const updateManningAgentOfficers = (idx: number, field: keyof ManningAgentOfficerM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.manningAgentOfficers];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, manningAgentOfficers: updated };
    });
  };

  const updateManningAgentRatings = (idx: number, field: keyof ManningAgentRatingM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.manningAgentRatings];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, manningAgentRatings: updated };
    });
  };

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  const updateForm = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Validate and/or proceed
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      let nextForm = form;

      if (nextForm.nationalityOfRatings.length === 0) {
        nextForm = {
          ...nextForm,
          nationalityOfRatings: [
            {
              ...initialBq3CrewManagement.nationalityOfRatings[0],
              nationalityId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm.manningAgentOfficers.length === 0) {
        nextForm = {
          ...nextForm,
          manningAgentOfficers: [
            {
              ...initialBq3CrewManagement.manningAgentOfficers[0],
              officerId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm.manningAgentRatings.length === 0) {
        nextForm = {
          ...nextForm,
          manningAgentRatings: [
            {
              ...initialBq3CrewManagement.manningAgentRatings[0],
              ratingId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm !== form) {
        setForm(nextForm);
        return;
      }

      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    setIsLoading(true);

    let crewManagementData = [];
    let nationalityOfRatingsData = [];
    let manningAgentOfficersData = [];
    let manningAgentRatingsData = [];

    try {
      // Crew Management
      try {
        const res = await api.get(`${apiUrl}/bq_CrewManagement`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        crewManagementData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Crew Management not found (404)");
        } else {
          console.error("Error fetching Crew Management", error);
        }
      }

      // Nationality Of Ratings
      try {
        const res = await api.get(`${apiUrl}/bq_NationalityOfRatings`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        nationalityOfRatingsData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Nationality Of Ratings not found (404)");
        } else {
          console.error("Error fetching Nationality Of Ratings", error);
        }
      }

      // Manning Agent Officers
      try {
        const res = await api.get(`${apiUrl}/bq_ManningAgentOfficers`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        manningAgentOfficersData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Manning Agent Officers not found (404)");
        } else {
          console.error("Error fetching Manning Agent Officers", error);
        }
      }

      // Manning Agent Ratings
      try {
        const res = await api.get(`${apiUrl}/bq_ManningAgentRatings`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        manningAgentRatingsData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Manning Agent Ratings not found (404)");
        } else {
          console.error("Error fetching Manning Agent Ratings", error);
        }
      }

      const _form = mapSqlToBq3CrewManagementForm(
        crewManagementData[0],
        nationalityOfRatingsData,
        manningAgentOfficersData,
        manningAgentRatingsData,
      );
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error) {
      console.error("Unexpected error fetching crew management data", error);
    } finally {
      setIsLoading(false);
    }
  };

  const addNationalityOfRatings = () => {
    let _nationalityOfRatings = [...form.nationalityOfRatings];
    _nationalityOfRatings.push({
      ...initialBq3CrewManagement.nationalityOfRatings[0],
      nationalityId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, nationalityOfRatings: _nationalityOfRatings }));
  };

  const removeNationalityOfRatings = (i: number) => {
    const id = form.nationalityOfRatings[i].nationalityId;
    deleteNationalityOfRatings(id);
    let _nationalityOfRatings = [...form.nationalityOfRatings];
    _nationalityOfRatings.splice(i, 1);
    setForm((prev) => ({ ...prev, nationalityOfRatings: _nationalityOfRatings }));
  };

  const addMao = () => {
    let _manningAgentOfficers = [...form.manningAgentOfficers];
    _manningAgentOfficers.push({
      ...initialBq3CrewManagement.manningAgentOfficers[0],
      officerId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, manningAgentOfficers: _manningAgentOfficers }));
  };

  const removeMao = (i: number) => {
    const id = form.manningAgentOfficers[i].officerId;
    deleteManningAgentOfficers(id);
    let _manningAgentOfficers = [...form.manningAgentOfficers];
    if (i === 0) {
      _manningAgentOfficers[0] = {
        ...initialBq3CrewManagement.manningAgentOfficers[0],
        id: _manningAgentOfficers[0].id, // Assign a new ID if needed
      };
    } else {
      _manningAgentOfficers.splice(i, 1);
    }
    setForm((prev) => ({ ...prev, manningAgentOfficers: _manningAgentOfficers }));
  };

  const addMar = () => {
    let _manningAgentRatings = [...form.manningAgentRatings];
    _manningAgentRatings.push({
      ...initialBq3CrewManagement.manningAgentRatings[0],
      ratingId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, manningAgentRatings: _manningAgentRatings }));
  };

  const removeMar = (i: number) => {
    const id = form.manningAgentRatings[i].ratingId;
    deleteManningAgentRatings(id);
    let _manningAgentRatings = [...form.manningAgentRatings];
    if (i === 0) {
      _manningAgentRatings[0] = {
        ...initialBq3CrewManagement.manningAgentRatings[0],
        id: _manningAgentRatings[0].id, // Assign a new ID if needed
      };
    } else {
      _manningAgentRatings.splice(i, 1);
    }
    setForm((prev) => ({ ...prev, manningAgentRatings: _manningAgentRatings }));
  };

  const deleteNationalityOfRatings = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelNationalityOfRatings`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Nationality Of Ratings not found (404)");
      } else {
        console.error("Error deleting Nationality Of Ratings", error);
      }
    }
  };

  const deleteManningAgentOfficers = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelManningAgentOfficer`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Manning Agent Officers not found (404)");
      } else {
        console.error("Error deleting Manning Agent Officers", error);
      }
    }
  };

  const deleteManningAgentRatings = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelManningAgentRating`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Manning Agent Ratings not found (404)");
      } else {
        console.error("Error deleting Manning Agent Ratings", error);
      }
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="text-green">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-6">Crew Management</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-4">
            {/* Master-Nationality */}
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">Master Nationality</label>
              <select
                disabled={readonly}
                name="masterNationality"
                value={form.masterNationality}
                onChange={updateForm}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                {nationalities.map((nationality) => (
                  <option key={nationality} value={nationality}>
                    {nationality}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* No of Officers */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold text-lightGrey">
                What is the actual number of officers on board?
              </label>
              <input
                disabled={readonly}
                type="number"
                name="numberOfOfficers"
                value={form.numberOfOfficers}
                onChange={updateForm}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* List nationality of officers */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold text-lightGrey">List Nationality of Officers</label>
              <textarea
                disabled={readonly}
                name="listNationalityOfOfficers"
                value={form.listNationalityOfOfficers}
                onChange={updateForm}
                rows={2}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Ratings Nationalities */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-4">
              <label className="block text-sm font-bold text-gray-700">
                List the nationality/nationalities of the ratings onboard
              </label>
            </div>
          </div>
          <div className={twMerge("mb-4 flex gap-2 border-b pb-2 text-xs font-bold", theme.uwhcAssistant.inputBorder)}>
            <div className="w-[2%]">
              <label>#</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Nationality</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Count</label>
            </div>
            <div className="w-[2%]"></div>
          </div>
          {form.nationalityOfRatings?.map((nr, i) => (
            <div key={i} className="mb-4 flex items-center gap-2">
              <div className="w-[2%]">{i + 1}</div>
              <div className="w-[20%]">
                <select
                  disabled={readonly}
                  name="nationality"
                  value={nr.nationality}
                  onChange={(e) => updateRatingNationality(i, "nationality", e.target.value)}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                >
                  <option value=""></option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={nr.count}
                  onChange={(e) => updateRatingNationality(i, "count", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>
              {!readonly && (
                <div className="w-[2%]">
                  <button className="mt-2 text-xs text-red-600" onClick={() => removeNationalityOfRatings(i)}>
                    <TrashIcon width={22} height={22} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {!readonly && (
            <button className="mb-8 inline-flex items-center rounded py-2 text-green" onClick={addNationalityOfRatings}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Nationality</span>
            </button>
          )}

          {/* Working language */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold text-lightGrey">
                What is the common working language on board?
              </label>
              <input
                disabled={readonly}
                type="text"
                name="commonWorkingLanguage"
                value={form.commonWorkingLanguage}
                onChange={updateForm}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Do officers speak English? */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold text-lightGrey">
                Do officers speak and understand English?
              </label>
              <div className="mt-1 flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="doOfficersSpeakEnglish"
                    value="yes"
                    checked={form.doOfficersSpeakEnglish === "yes"}
                    onChange={updateForm}
                    className="form-radio text-green"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="doOfficersSpeakEnglish"
                    value="no"
                    checked={form.doOfficersSpeakEnglish === "no"}
                    onChange={updateForm}
                    className="form-radio text-green"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              <label className="block text-sm font-bold text-gray-700">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("doOfficersSpeakEnglishComments")}
                >
                  Comments
                </button>
              </label>
              {(form.doOfficersSpeakEnglishComments || isCommentsVisible("doOfficersSpeakEnglishComments")) && (
                <textarea
                  disabled={readonly}
                  name="doOfficersSpeakEnglishComments"
                  value={form.doOfficersSpeakEnglishComments}
                  onChange={updateForm}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          {/* Manning agent for officers */}
          <div className="mb-8 grid grid-cols-4 gap-4">
            <div className="col-span-4">
              <label className="block text-sm font-bold text-gray-700">Manning agent for officers</label>
            </div>
          </div>
          <div className={twMerge("mb-4 flex gap-2 border-b pb-2 text-xs font-bold", theme.uwhcAssistant.inputBorder)}>
            <div className="w-[2%]">
              <label>#</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Company Name</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Address</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Phone</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Fax</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Email</label>
            </div>
            <div className="w-[2%]"></div>
          </div>
          {form.manningAgentOfficers?.map((mao, i) => (
            <div key={mao.id} className="mb-4 flex items-center gap-2">
              <div className="w-[2%]">{i + 1}</div>
              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mao.companyName}
                  onChange={(e) => updateManningAgentOfficers(i, "companyName", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mao.address}
                  onChange={(e) => updateManningAgentOfficers(i, "address", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mao.phone}
                  onChange={(e) => updateManningAgentOfficers(i, "phone", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mao.fax}
                  onChange={(e) => updateManningAgentOfficers(i, "fax", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mao.email}
                  onChange={(e) => updateManningAgentOfficers(i, "email", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>
              {!readonly && (
                <div className="w-[2%]">
                  <button className="mt-2 text-xs text-red-600" onClick={() => removeMao(i)}>
                    <TrashIcon width={22} height={22} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {!readonly && (
            <button className="mb-8 inline-flex items-center rounded py-2 text-green" onClick={addMao}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Agent</span>
            </button>
          )}

          {/* Manning agent for ratings (if different) */}
          <div className="mb-8 grid grid-cols-4 gap-4">
            <div className="col-span-4">
              <label className="block text-sm font-bold text-gray-700">
                Manning agent for ratings (if different to Officers)
              </label>
            </div>
          </div>
          <div className={twMerge("mb-4 flex gap-2 border-b pb-2 text-xs font-bold", theme.uwhcAssistant.inputBorder)}>
            <div className="w-[2%]">
              <label>#</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Company Name</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Address</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Phone</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Fax</label>
            </div>
            <div className="w-[20%]">
              <label className="mb-1 block text-xs font-bold">Email</label>
            </div>
            <div className="w-[2%]"></div>
          </div>
          {form.manningAgentRatings?.map((mar, i) => (
            <div key={mar.id} className="mb-4 flex items-center gap-2">
              <div className="w-[2%]">{i + 1}</div>
              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mar.companyName}
                  onChange={(e) => updateManningAgentRatings(i, "companyName", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mar.address}
                  onChange={(e) => updateManningAgentRatings(i, "address", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mar.phone}
                  onChange={(e) => updateManningAgentRatings(i, "phone", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mar.fax}
                  onChange={(e) => updateManningAgentRatings(i, "fax", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[20%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mar.email}
                  onChange={(e) => updateManningAgentRatings(i, "email", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
                />
              </div>
              {!readonly && (
                <div className="w-[2%]">
                  <button className="mt-2 text-xs text-red-600" onClick={() => removeMar(i)}>
                    <TrashIcon width={22} height={22} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {!readonly && (
            <button className="mb-8 inline-flex items-center rounded py-2 text-green" onClick={addMar}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Agent</span>
            </button>
          )}

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
